import { Popup } from "@tql/ui-components-react/kendo/popup";
import { DropDownList } from "@tql/ui-components-react/tql/dropdowns";
import React, { useCallback, useRef, useState } from "react";

import { usePopupOutsideClick } from "~/shared/hooks/use-popup-outside-click.ts";

import styles from "./slider-filter.module.css";

type SliderFilterRenderProps = {
	closePopup: () => void;
};

type SliderFilterProps = {
	children:
		| React.ReactNode
		| ((props: SliderFilterRenderProps) => React.ReactNode);
	hasValidRange: boolean;
	label: string;
	popupTestId: string;
	selectedText: string;
	triggerTestId: string;
};

type DropDownValueElement = React.ReactElement<{
	children?: React.ReactNode;
}>;

export const SliderFilter = ({
	children,
	hasValidRange,
	label,
	popupTestId,
	selectedText,
	triggerTestId,
}: SliderFilterProps) => {
	const [showPopup, setShowPopup] = useState(false);
	const anchorRef = useRef<HTMLDivElement>(null);
	const popupRef = useRef<HTMLDivElement>(null);

	const togglePopup = () => {
		setShowPopup((prev) => !prev);
	};

	const closePopup = useCallback(() => {
		setShowPopup(false);
	}, []);

	const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
		if (event.key === "Enter" || event.key === " ") {
			event.preventDefault();
			togglePopup();
		}
	};

	usePopupOutsideClick({
		anchorRef,
		enabled: showPopup,
		onOutsideClick: closePopup,
		popupRef,
	});

	const valueRender = (element: DropDownValueElement) => {
		return React.cloneElement(
			element,
			element.props,
			<>
				<span className={styles.sliderFilterLabel}>{label}</span>
				{selectedText ? (
					<span className={styles.sliderFilterValue}>{selectedText}</span>
				) : null}
			</>,
		);
	};

	let popupContent: React.ReactNode = <div>No range available</div>;
	if (hasValidRange) {
		popupContent =
			typeof children === "function" ? children({ closePopup }) : children;
	}

	return (
		<div ref={anchorRef}>
			<div
				aria-expanded={showPopup}
				aria-haspopup={true}
				data-testid={triggerTestId}
				onClick={togglePopup}
				onKeyDown={onKeyDown}
				role={"button"}
				tabIndex={0}
			>
				<DropDownList
					defaultItem={selectedText ? `${label} ${selectedText}` : label}
					opened={false}
					valueRender={valueRender}
				/>
			</div>
			<Popup
				anchor={anchorRef.current}
				anchorAlign={{ horizontal: "left", vertical: "bottom" }}
				animate={false}
				popupAlign={{ horizontal: "left", vertical: "top" }}
				show={showPopup}
			>
				<div
					ref={popupRef}
					className={styles.sliderFilterPopup}
					data-testid={popupTestId}
				>
					{popupContent}
				</div>
			</Popup>
		</div>
	);
};

export const SliderFilterInputs = ({
	children,
}: {
	children: React.ReactNode;
}) => {
	return <div className={styles.sliderFilterPopupInputs}>{children}</div>;
};

export const SliderFilterInput = ({
	children,
	disabled = false,
}: {
	children: React.ReactNode;
	disabled?: boolean;
}) => {
	const className = disabled
		? `${styles.sliderFilterPopupInput} ${styles.sliderFilterDisabledValue}`
		: styles.sliderFilterPopupInput;

	return <div className={className}>{children}</div>;
};

export const SliderFilterActions = ({
	children,
}: {
	children: React.ReactNode;
}) => {
	return <div className={styles.sliderFilterPopupActions}>{children}</div>;
};

export const SliderFilterField = ({
	children,
	label,
	required = false,
}: {
	children: React.ReactNode;
	label: string;
	required?: boolean;
}) => {
	return (
		<div className={styles.sliderFilterField}>
			<span className={styles.sliderFilterFieldLabel}>
				{label}
				{required ? (
					<span
						aria-hidden={true}
						className={styles.sliderFilterRequiredIndicator}
					>
						{" *"}
					</span>
				) : null}
			</span>
			{children}
		</div>
	);
};
